import React from "react";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import { BASE_URL } from "../../constant/constant";
import { useAuth0Context } from "../../context/Auth0Context";
import "../../styles/AppOrgSection.css";
import { getToolTipAttributes } from "../../utils/tooltip";

// Helper: Returns object { dateStr, timeStr }
const getFormattedDate = (dateString) => {
  if (!dateString) return { dateStr: "Unknown Date", timeStr: "" };
  if (dateString.startsWith("0001")) return { dateStr: "N/A", timeStr: "" };

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return { dateStr: "Invalid Date", timeStr: "" };

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleString("en-US", { hour: "numeric", minute: "numeric", hour12: true });

    return {
      dateStr: `${day}-${month}-${year}`,
      timeStr: time
    };
  } catch (error) {
    return { dateStr: "Error Date", timeStr: "" };
  }
};

const LockedApplicationCard = ({ app, lockData, onUnlock, openApp }) => {
  // --- 1. Get Logged In User & Roles ---
  const { user } = useAuth0Context();

  // Check Permissions: Super Admin
  const isSuperAdmin = user?.role_names?.includes("super-admin");

  // Destructure the date and time
  const { dateStr, timeStr } = getFormattedDate(lockData?.locked_at);

  // --- 2. User Name Display Logic ---
  let fullName = "Unknown User";
  let email = "";
  if (lockData?.user) {
    const { first_name, last_name, email: userEmail } = lockData.user;
    if (first_name || last_name) {
      fullName = `${first_name || ""} ${last_name || ""}`.trim();
    } else {
      fullName = userEmail || "Unknown User";
    }
    email = userEmail || "";
  }

  return (
    <div className="app_item_box locked_app_box" key={app.id}>
      {/* Header */}
      <div className="locked_card_header">
        {/* CLICKABLE LOGO SECTION to Open App */}
        {/* We make the whole row clickable for better UX */}
        <div className="locked_logo_section cursor_pointer" onClick={() => openApp && openApp(app, app)}>
          {app?.logo ? (
            <div
              className="org_logo_circle grayscale_img"
              style={{
                backgroundImage: `url(${BASE_URL}/api/v1/public/${app.logo})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center"
              }}
            />
          ) : (
            <MaterialIcon name="deployed_code" className="default_org_img org_logo_circle grayscale_img" />
          )}

          <div className="app_item_title locked_title" {...getToolTipAttributes(app?.name)}>
            {app?.name}
          </div>

          <MaterialIcon
            name="open_in_new"
            {...getToolTipAttributes("Open Application (Read Only)")}
            style={{ color: "#666", margin: "0 4px", fontSize: "16px" }}
            className="cursor_pointer"
          />
        </div>

        <div className="locked_icon_wrapper">
          <MaterialIcon name="lock" className="locked_status_icon" />
        </div>
      </div>

      {/* Info Grid */}
      <div className="locked_info_grid">
        {/* Left Column: User Info */}
        <div className="info_col">
          <label className="info_label">Locked By</label>
          <div className="info_value_bold" title={fullName}>
            {fullName} {isSuperAdmin ? "(You)" : ""}
          </div>
          <div className="info_value_small" title={email}>
            {email}
          </div>
        </div>

        {/* Right Column: Date & Time (Split) */}
        <div className="info_col right_align">
          <label className="info_label">Locked On</label>
          <div className="info_value_date">{dateStr}</div>
          <div className="info_value_small" style={{ marginTop: "2px", color: "#888" }}>
            {timeStr}
          </div>
        </div>
      </div>

      <div className="d_flex align_end justify_between w_100 mt_auto">
        {/* Environment Status */}
        <div className="locked_env_row">
          <div className="env_item">
            <span className="env_label">Stage:</span>
            <span className={`env_pill ${app?.staging_status?.toLowerCase() || "stopped"}`}>
              {app?.staging_status || "-"}
            </span>
          </div>
          <div className="env_item">
            <span className="env_label">Prod:</span>
            <span className={`env_pill ${app?.status?.toLowerCase() || "stopped"}`}>{app?.status || "-"}</span>
          </div>
        </div>

        {/* Footer: Unlock Button */}
        <div className="locked_actions_footer">
          {isSuperAdmin ? (
            <button
              className="unlock_full_width_btn"
              onClick={(e) => {
                e.stopPropagation();
                onUnlock();
              }}
            >
              <MaterialIcon name="lock_open" />
              <span>Unlock</span>
            </button>
          ) : (
            <button
              className="unlock_full_width_btn disabled"
              {...getToolTipAttributes(`Only Super Admins can unlock this`)}
              onClick={(e) => e.stopPropagation()}
              style={{ pointerEvents: "auto" }}
            >
              <MaterialIcon name="block" />
              <span>Locked</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LockedApplicationCard;
